import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApplicantAuth } from "@/context/ApplicantAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import PhoneOtpLogin from "@/components/PhoneOtpLogin";

const ApplicantLogin = () => {
  const nav = useNavigate();
  const { login, setApplicant } = useApplicantAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/careers/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const onOtpSuccess = ({ access_token, user }) => {
    localStorage.setItem("ca_applicant_token", access_token);
    setApplicant?.(user);
    toast.success("Signed in.");
    window.location.href = "/careers/dashboard";
  };

  return (
    <main data-testid="applicant-login-page" className="min-h-screen flex flex-col bg-background">
      <div className="p-6">
        <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={14} /> Careers
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Applicant portal</div>
              <div className="font-serif-display text-2xl">Sign in</div>
            </div>
          </div>

          <Tabs defaultValue="password">
            <TabsList className="grid grid-cols-2 mb-6 rounded-full bg-muted p-1 h-11">
              <TabsTrigger value="password" data-testid="applicant-tab-password" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Email &amp; password</TabsTrigger>
              <TabsTrigger value="otp" data-testid="applicant-tab-otp" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Text me a code</TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <form onSubmit={submit} data-testid="applicant-login-form" className="space-y-4">
                <div>
                  <Label htmlFor="ae" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                  <Input id="ae" data-testid="input-applicant-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11 rounded-lg" autoComplete="username" />
                </div>
                <div>
                  <Label htmlFor="ap" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
                  <Input id="ap" data-testid="input-applicant-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-11 rounded-lg" autoComplete="current-password" />
                </div>
                <Button type="submit" data-testid="applicant-login-submit" disabled={busy} className="mt-2 w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="otp">
              <PhoneOtpLogin purpose="applicant" onSuccess={onOtpSuccess} allowSignup={false} />
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-xs text-center text-muted-foreground">
            No account yet? <Link to="/careers/apply" className="text-primary hover:underline">Start an application</Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export default ApplicantLogin;
